/**
 * Platform credential administration — the shapes the API exposes.
 *
 * PascalCase throughout: these mirror the C# DTOs on `/PlatformCredential`. The one thing to
 * understand about this contract is what is ABSENT — there is no field carrying a credential value
 * and there is no endpoint that returns one. The server is write-only for secrets; the most the
 * client ever sees is `Last4`. If a "reveal" field ever appears here, something has gone wrong.
 */

/** Where a credential actually resolves from. */
export type CredentialSource = 'Stored' | 'Configuration' | 'NotConfigured';

/** What a capability means: which kind of model a function needs. */
export type AiCapability = 'Text' | 'TextVision' | 'ImageGeneration' | 'Segmentation' | 'ImageEdit';

export interface CredentialStatus {
  CredentialKey: string;
  IsSet: boolean;
  /** Last four characters, or null when the value is too short to reveal any of safely. */
  Last4: string | null;
  KeyVersion: string | null;
  IsActive: boolean;
  ModifiedDate: string | null;
  ModifiedBy: string | null;
  /**
   * `Stored` — a row in the encrypted store is in force.
   * `Configuration` — falling through to the server's env/appsettings value.
   * `NotConfigured` — nothing anywhere.
   */
  Source: CredentialSource;
}

export interface AiProviderCredential {
  Key: string;
  Name: string;
  CredentialKey: string;
  DefaultModel: string | null;
  CostPerMTokens: number | null;
  Capabilities: AiCapability[];
  /** Null is possible: the server matches statuses to providers and may find none for a key. */
  Status: CredentialStatus | null;
}

export interface PlatformCredentialsResponse {
  /** False when no master key is configured server-side — saving will fail until it is. */
  EncryptionConfigured: boolean;
  Providers: AiProviderCredential[];
}

/** The outcome of a connection probe. Carries no credential material. */
export interface AiProbeResult {
  Ok: boolean;
  Provider: string;
  Model: string | null;
  ResponseTimeMs: number | null;
  Error: string | null;
}

export class SaveCredentialRequest {
  CredentialKey: string = '';
  Value: string = '';
}

export class TestCredentialRequest {
  Provider: string = '';
  /** Omit to test the STORED credential; supply to test a candidate not yet saved. */
  Value?: string;
  Model?: string;
}

export interface PlatformCredentialAudit {
  Id: string;
  CredentialKey: string;
  Action: string;
  PerformedBy: string | null;
  PerformedByEmail: string | null;
  PerformedDate: string;
  Outcome: string | null;
  Detail: string | null;
  IpAddress: string | null;
}

/**
 * AI function binding — which provider serves each AI-requiring feature.
 *
 * `Candidates` is already filtered by capability server-side, so the screen can render it directly:
 * an option that appears here is one that can actually run.
 */
export interface AiFunctionResolution {
  FunctionKey: string;
  Capability: string;
  ProviderKey: string | null;
  ProviderName: string | null;
  Model: string | null;
  IsEnabled: boolean;
  HasCredential: boolean;
  /** Binding | FunctionDefault | Substitute | None */
  Source: string;
  /** Plain-English account of why this provider was chosen. Show it — a silent fallback misleads. */
  ResolutionNote: string;
  CanRun: boolean;
}

export interface AiFunctionCandidate {
  Key: string;
  Name: string;
  DefaultModel: string | null;
}

export interface AiFunctionView {
  Key: string;
  Name: string;
  Description: string;
  Capability: string;
  DefaultProviderKey: string;
  AssignedProviderKey: string | null;
  AssignedModel: string | null;
  IsEnabled: boolean;
  Resolution: AiFunctionResolution | null;
  Candidates: AiFunctionCandidate[];
}

export class SaveFunctionBindingRequest {
  FunctionKey: string = '';
  /** Blank clears the assignment and returns the feature to its default. */
  ProviderKey: string | null = null;
  ModelId: string | null = null;
  IsEnabled: boolean = true;
}

/** One provider's connectivity, as the last check saw it. */
export interface AiProviderHealthView {
  ProviderKey: string;
  ProviderName: string;
  /** Healthy | Failing | NotConfigured | Stale */
  Status: string;
  LastCheckedDate: string | null;
  LastOkDate: string | null;
  ConsecutiveFailures: number;
  LastError: string | null;
  ResponseTimeMs: number | null;
}

export interface AiHealthReport {
  /** Healthy | Degraded | Critical | Unconfigured | Stale */
  Overall: string;
  LastCheckedDate: string | null;
  /** True when the CHECKER has stopped — a worse problem than a provider being down. */
  IsStale: boolean;
  /** False when no Email/SMS action is wired to the alert events, so nobody would hear about an outage. */
  AlertingConfigured: boolean;
  Providers: AiProviderHealthView[];
  Configured: number;
  Healthy: number;
  Failing: number;
}

/** The spend tripwire's view. Coarse by design — counts of generations, not currency. */
export interface AiSpendReport {
  GenerationsLast7Days: number;
  GenerationsPrevious7Days: number;
  WeekOnWeekRatio: number | null;
  TopUserId: string | null;
  TopUserGenerations: number;
  MedianUserGenerations: number;
  IsAnomalous: boolean;
  Reason: string;
}
